import React from 'react'
import { IconButton, Box, Flex, Button, useBoolean, Drawer, DrawerContent, DrawerOverlay, useDisclosure, useColorModeValue, Icon, FlexProps, BoxProps } from '@chakra-ui/react'
import { useToastify } from '@/hooks/useToastify'
import { ConfirmDialog } from '@zumma/ui'
import { FaDoorOpen, FaQuestion } from 'react-icons/fa'
import { FiMenu } from 'react-icons/fi'
import { useDispatch } from 'react-redux'
import { logOut } from '../../store/user/slice'
import { useAppSelector } from '../../store/hooks'
import { RouterObj, routerObj, ADMIN_LAYOUT, REVIEWER_LAYOUT, EMPLOYEE_LAYOUT } from '../routing/Router'
import { colors } from '../../utils/colors'
import { rounded, buttonStyles } from '../../utils/ui'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { ReactSVG } from 'react-svg'
//@ts-ignore
import ZummaBannerLogo from '../../assets/svg/ZummaBannerLogo.svg'
import { clearInvoicesState } from '../../store/invoices/slice'
import { setError } from '../../store/ui/slice'
import { SendInvoiceButton } from '../ui/SendInvoiceButton'
import { ReferralDialog } from '../ui/ReferralDialog'
import { UserRole } from '../../ts/interfaces/interfaces'
import { Button as ZummaButton } from '@zumma/ui'
import { selectHasBankReconciliation } from '../../store/subscription/slice'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from './LanguageSwitcher'
const NavItem = React.memo(
  (
    props: FlexProps & {
      icon: any
      path: string
      sidebar: {
        isOpen: boolean
        onOpen: () => void
        onClose: () => void
        onToggle: () => void
        isControlled: boolean
        getButtonProps: (props?: any) => any
        getDisclosureProps: (props?: any) => any
      }
    }
  ) => {
    const { icon, children, path, ...rest } = props
    const navigate = useNavigate()

    return (
      <Link
        id={`nav-link-${path}`}
        to={path}
        onClick={(e) => {
          e.preventDefault()
          navigate(path)
          props.sidebar.onClose()
        }}
      >
        <Flex
          maxW='100%'
          rounded={rounded.container}
          align='center'
          pl='4'
          cursor='pointer'
          color='inherit'
          _dark={{
            color: 'gray.400'
          }}
          _hover={{
            bg: 'gray.100',
            _dark: {
              bg: 'gray.900'
            },
            color: 'gray.700'
          }}
          bg={path === location.pathname ? colors.tertiary[3] : 'initial'}
          role='group'
          transition='.15s ease'
          {...buttonStyles}
          {...rest}
        >
          {icon && (
            <Icon
              mx='2'
              boxSize='4'
              _groupHover={{
                color: 'gray.700'
              }}
              as={icon}
            />
          )}
          {children}
        </Flex>
      </Link>
    )
  }
)

const SidebarContent = React.memo(
  ({
    sidebar,
    setShowLogOut,
    paths = [],
    role,
    ...props
  }: BoxProps & {
    paths: Array<RouterObj>
    sidebar: {
      isOpen: boolean
      onOpen: () => void
      onClose: () => void
      onToggle: () => void
      isControlled: boolean
      getButtonProps: (props?: any) => any
      getDisclosureProps: (props?: any) => any
    }
    setShowLogOut: {
      on: () => void
      off: () => void
      toggle: () => void
    }
    role: UserRole
  }) => {
    const { t } = useTranslation()

    return (
      <Flex
        px='4'
        flexDir={{
          base: 'column'
        }}
        justifyContent={{
          base: 'start'
        }}
        as='nav'
        pos='fixed'
        top='0'
        left='0'
        h='100%'
        pb='10'
        overflowX='hidden'
        overflowY='auto'
        _dark={{
          bg: 'gray.800'
        }}
        color='inherit'
        borderRightWidth='1px'
        w='60'
        {...props}
      >
        <Flex
          px='4'
          py='5'
          align='center'
        >
          {/* <Logo /> */}

          <Link
            to={(() => {
              if (role === UserRole.ADMIN) return routerObj.home.path
              else return routerObj.expensesDashboard.path
            })()}
          >
            <ReactSVG src={ZummaBannerLogo} />
          </Link>
        </Flex>
        <Flex
          direction='column'
          as='nav'
          fontSize='sm'
          aria-label='Main Navigation'
          rowGap='0.2rem'
        >
          {paths.map((path) =>
            path.label && path.icon ? (
              <NavItem
                key={`nav-item-${path.path}`}
                path={path.path}
                icon={path.icon}
                sidebar={sidebar}
              >
                {typeof path.label === 'object' ? t(path.label[role]) : t(path.label)}
              </NavItem>
            ) : null
          )}
        </Flex>

        <Flex
          marginTop='auto'
          flexDir='column'
          gap='1rem'
        >
          <SendInvoiceButton label={t('layout.goToWhatsApp')} />
          <ReferralDialog />
          <ZummaButton
            justifySelf='end'
            id='logOutButton'
            onClick={setShowLogOut.toggle}
            variant='deny'
          >
            {t('layout.logout')}
            <FaDoorOpen />
          </ZummaButton>

          <ZummaButton
            justifySelf='end'
            id='needHelpButton'
            onClick={() => {
              window.open(process.env.REACT_APP_HELP_URL, '_blank')
            }}
            variant='tertiary'
          >
            {t('layout.needHelp')}{' '}
            <Icon
              color='gray.500'
              as={FaQuestion}
              cursor='pointer'
            />
          </ZummaButton>
        </Flex>
      </Flex>
    )
  }
)

const Layout = React.memo(() => {
  const token = useAppSelector((state) => state.user.token)
  const userRole = useAppSelector((state) => state.user.role)
  const hasBankReconciliation = useAppSelector(selectHasBankReconciliation)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToastify()
  const { t } = useTranslation()

  const PATHS: Array<RouterObj> = React.useMemo(() => {
    let paths: Array<RouterObj> = []
    if (userRole == UserRole.ADMIN) paths = ADMIN_LAYOUT
    else if (userRole == UserRole.REVIEWER) paths = REVIEWER_LAYOUT
    else if (userRole == UserRole.EMPLOYEE) paths = EMPLOYEE_LAYOUT

    // Only show conciliation link if user has the bank reconciliation product
    if (!hasBankReconciliation || userRole !== UserRole.ADMIN) {
      paths = paths.filter((p) => p.path !== routerObj.conciliationDashboard.path)
    }

    return paths
  }, [userRole, hasBankReconciliation])

  const ui = useAppSelector((state) => state.ui)
  const [showLogOut, setShowLogOut] = useBoolean(false)

  const sidebar = useDisclosure()
  const unauthRoutes = React.useMemo(() => {
    const list = [routerObj.signUp.path, routerObj.signIn.path, routerObj.recoverPassword.path, routerObj.renewPassword.path, routerObj.requestAccess.path, routerObj.accessRequestAction.path, routerObj.accessRequestForm.path, routerObj.accessRequestOwnAccount.path]
    return list
  }, [routerObj, userRole])

  const invalidEmployeeRoutes = React.useMemo(() => {
    const list = [routerObj.home.path, routerObj.pricing.path, routerObj.businessDashboard.path, routerObj.usersDashboard.path]

    return list
  }, [routerObj, userRole])

  React.useEffect(() => {
    if (ui.errorMessage) {
      toast.error(t('shared.layout.errorToast', { status: ui.errorStatus ? ui.errorStatus : '', message: ui.errorMessage }), {
        autoClose: 8000
      })
      dispatch(setError(null))
    }
  }, [ui.errorMessage, ui.errorStatus, ui.isFetching, t])

  React.useEffect(() => {
    if (!token && !unauthRoutes.includes(location.pathname)) {
      navigate(routerObj.signIn.path)
    } else if ((token && unauthRoutes.includes(location.pathname) && location.pathname !== routerObj.accessRequestOwnAccount.path) || '/' == location.pathname) {
      // The own-account link is addressed to a signed-out requester; a stale
      // session left in this browser must not swallow it, so it is excluded
      // from the redirect above rather than sent home like the other public
      // pages.
      // A signed-in admin on the email action page carries the signed token to
      // the users dashboard, where it opens the confirmation dialog. The
      // generic home redirect would otherwise win the race against that page's
      // own navigation (this effect runs after the page's).
      const emailLinkToken = location.pathname === routerObj.accessRequestAction.path ? new URLSearchParams(location.search).get('token') : null
      if (emailLinkToken && userRole === UserRole.ADMIN) {
        navigate(`${routerObj.usersDashboard.path}?access_request_token=${encodeURIComponent(emailLinkToken)}`, { replace: true })
      } else if (userRole === UserRole.ADMIN) navigate(routerObj.home.path)
      else navigate(routerObj.expensesDashboard.path)
    } else if (token && userRole === UserRole.EMPLOYEE && invalidEmployeeRoutes.includes(location.pathname)) {
      navigate(routerObj.expensesDashboard.path)
    }
  }, [token, location, routerObj, userRole])

  React.useEffect(() => {
    if (![routerObj.expensesDashboard.path].includes(location.pathname)) {
      window.scrollTo(0, 0)
    }
  }, [location])

  const forbiddenPaths = React.useMemo(() => [routerObj.welcomeSubscriptionPage.path, routerObj.renewPassword.cleanParamsPath], [routerObj])
  const noPaddingPaths = React.useMemo(() => [routerObj.signIn.path, routerObj.signUp.path, routerObj.recoverPassword.path, routerObj.renewPassword.path, routerObj.requestAccess.path, routerObj.accessRequestAction.path, routerObj.accessRequestForm.path, routerObj.accessRequestOwnAccount.path], [routerObj])

  const handleLogOut = React.useCallback(() => {
    setShowLogOut.off()
    sidebar.onClose()
    dispatch(logOut())
    dispatch(clearInvoicesState())
    navigate(routerObj.signIn.path)
  }, [navigate, setShowLogOut, sidebar, dispatch, clearInvoicesState, logOut])

  if (!token || forbiddenPaths.find((path) => location.pathname.match(path ?? '')))
    return (
      <Box
        bg={colors.neutro[3]}
        as='main'
        maxW='100%'
        height='100vh'
        p={noPaddingPaths.includes(location.pathname) ? '0' : '1rem'}
      >
        <Outlet />
      </Box>
    )

  return (
    <Box
      as='section'
      bg='gray.50'
      _dark={{
        bg: 'gray.700'
      }}
      minH='100vh'
      position='relative'
      zIndex='sticky'
      width='100%'
    >
      <SidebarContent
        paths={PATHS}
        role={userRole}
        setShowLogOut={setShowLogOut}
        display={{
          base: 'none',
          lg: 'flex'
        }}
        sidebar={sidebar}
      />
      <Drawer
        isOpen={sidebar.isOpen}
        onClose={sidebar.onClose}
        placement='left'
      >
        <DrawerOverlay />
        <DrawerContent>
          <SidebarContent
            paths={PATHS}
            role={userRole}
            setShowLogOut={setShowLogOut}
            sidebar={sidebar}
            w='full'
            borderRight='none'
          />
        </DrawerContent>
      </Drawer>
      <Box
        ml={{
          base: 0,
          lg: 60
        }}
        transition='.3s ease'
      >
        {/* search bard */}
        <Flex
          id='LayoutHeader'
          as='header'
          alignItems='center'
          justify='end'
          w='full'
          px='4'
          zIndex='sticky'
          position='sticky'
          top={0}
          bg={colors.white}
          borderBottomWidth='1px'
          color='inherit'
          h='14'
          gap={{
            base: '0.5rem',
            sm: '1rem'
          }}
          flexDir={{
            base: 'row-reverse',
            lg: 'row'
          }}
          style={{
            zIndex: 9999
          }}
        >
          <IconButton
            aria-label='Menu'
            display={{
              base: 'inline-flex',
              lg: 'none'
            }}
            onClick={sidebar.onOpen}
            icon={<FiMenu />}
            size='sm'
          />

          <LanguageSwitcher />
          <SendInvoiceButton />
        </Flex>

        {/* CHILDREN */}
        <Box
          bg={colors.neutro[3]}
          as='main'
          minW='100%'
          maxW='fit-content'
          minH='100vh'
          maxH='100%'
        >
          <Outlet />
          {ui.isFetching ? (
            <div className='spinner'>
              <div></div>
            </div>
          ) : null}
        </Box>
      </Box>

      <ConfirmDialog
        confirmMethod={handleLogOut}
        isOpen={showLogOut}
        title={t('layout.logoutConfirm')}
        onClose={setShowLogOut.off}
      />
    </Box>
  )
})

export { Layout }
